// File: src/components/dashboard/ArtistMonthlyPointsChart.js

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { useProfile } from '../../context/ProfileContext';
import confetti from 'canvas-confetti';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  Cell 
} from 'recharts';

/**
 * 🎨 6-Color Palette: Yellow, Blue, Red, Green, Orange, Purple
 */
const BAR_COLORS = [
  '#eab308', // Yellow (Month 1)
  '#3b82f6', // Blue   (Month 2)
  '#ef4444', // Red    (Month 3)
  '#22c55e', // Green  (Month 4)
  '#f97316', // Orange (Month 5)
  '#a855f7', // Purple (Month 6 / Current Month)
];

/**
 * Helper: Converts "MM-YYYY" string into a Date object for sorting
 */
const parseMonthKey = (key) => {
  const [month, year] = key.split('-').map(Number);
  return new Date(year, month - 1, 1);
};

/**
 * Helper: Formats "MM-YYYY" into a short month label without the year (e.g., "Oct")
 */
const formatMonthLabel = (key) => {
  const date = parseMonthKey(key);
  return date.toLocaleDateString('en-US', { month: 'short' });
};

/**
 * Helper: Generates an array of the last N month keys ("MM-YYYY") ending at current month
 */
const getLastNMonthsKeys = (count = 6) => {
  const keys = [];
  const now = new Date();
  
  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    keys.push(`${month}-${year}`);
  }
  return keys;
};

const ArtistMonthlyPointsChart = () => {
  const { activeProfile, isDarkMode } = useProfile();

  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const chartContainerRef = useRef(null);
  const hasTriggeredConfetti = useRef(false);

  const artistId = activeProfile?.type === 'artist' ? activeProfile.id : null;

  useEffect(() => {
    if (!artistId) {
      setChartData([]);
      setLoading(false);
      return;
    }

    const db = getFirestore();
    const artistDocRef = doc(db, 'artist_accounts', artistId);

    const unsubscribe = onSnapshot(artistDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const rawMonthlyPoints = docSnap.data().monthlyPoints || {};
        const recentKeys = getLastNMonthsKeys(6);

        const formattedData = recentKeys.map((key) => ({
          rawKey: key,
          label: formatMonthLabel(key),
          points: rawMonthlyPoints[key] || 0,
          isCurrentMonth: key === recentKeys[recentKeys.length - 1],
        }));

        setChartData(formattedData);

        // 🎉 Trigger celebratory confetti if points were earned in the current month
        const currentMonthItem = formattedData[formattedData.length - 1];
        if (currentMonthItem && currentMonthItem.points > 0 && !hasTriggeredConfetti.current) {
          hasTriggeredConfetti.current = true;
          
          setTimeout(() => {
            if (chartContainerRef.current) {
              const rect = chartContainerRef.current.getBoundingClientRect();
              
              const originX = (rect.right - 25) / window.innerWidth;
              const originY = (rect.top + rect.height / 2) / window.innerHeight;

              confetti({
                particleCount: 45,
                spread: 60,
                origin: { x: originX, y: originY },
                colors: BAR_COLORS,
                disableForReducedMotion: true,
                zIndex: 1000,
              });
            }
          }, 350);
        }
      } else {
        setChartData([]);
      }
      setLoading(false);
    }, (err) => {
      console.error("Error loading monthly points data:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [artistId]);

  // Determine if any points exist across the entire 6-month period
  const hasPoints = useMemo(() => {
    return chartData.some((item) => item.points > 0);
  }, [chartData]);

  // Extract points specifically earned this month (last element in chartData)
  const thisMonthPoints = useMemo(() => {
    if (chartData.length === 0) return 0;
    return chartData[chartData.length - 1]?.points || 0;
  }, [chartData]);

  if (loading) {
    return (
      <div className={`p-2.5 rounded-xl border text-center animate-pulse ${
        isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-500' : 'bg-white border-slate-100 text-slate-400'
      }`}>
        <p className="text-[9px] font-black uppercase tracking-widest">Loading Points...</p>
      </div>
    );
  }

  return (
    <div 
      ref={chartContainerRef}
      className={`px-3 py-2 rounded-xl border shadow-sm transition-all ${
        isDarkMode ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-slate-100 text-slate-900'
      }`}
    >
      {/* 
        1. Tight Header Row:
        - Subtitle removed.
        - Title aligned inline with the badge.
        - Badge reduced in height, padding, and font-size.
      */}
      <div className="flex justify-between items-center mb-1">
        <h3 className="text-[11px] font-black uppercase tracking-wider italic leading-none">
          Fan Points Earned
        </h3>

        {/* Compact "This Month" Badge */}
        <div className={`px-2 py-0.5 rounded-lg border flex items-center gap-1 ${
          isDarkMode 
            ? 'bg-zinc-800/80 border-zinc-700/60 text-zinc-200' 
            : 'bg-slate-50 border-slate-200 text-slate-800'
        }`}>
          <span className="text-[8px] font-bold uppercase tracking-wider text-gray-400 leading-none">
            This Month:
          </span>
          <span className="text-[10px] font-black text-brand-pink leading-none">
            {Number(thisMonthPoints).toLocaleString()} pts
          </span>
        </div>
      </div>

      {/* 
        2. Compressed Chart Canvas:
        - Reduced container height to h-16 (from h-20).
        - Labels render month only (e.g., "Oct").
      */}
      <div className="w-full h-16 relative">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData} 
            margin={{ top: 2, right: 4, left: -28, bottom: -6 }}
          >
            <XAxis 
              dataKey="label" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: isDarkMode ? '#a1a1aa' : '#64748b', fontSize: 9, fontWeight: 700 }} 
            />
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: isDarkMode ? '#71717a' : '#94a3b8', fontSize: 8 }}
              domain={[0, (dataMax) => (dataMax > 0 ? dataMax : 4)]}
            />

            {hasPoints && (
              <Tooltip 
                cursor={{ fill: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}
                contentStyle={{
                  backgroundColor: isDarkMode ? '#18181b' : '#ffffff',
                  borderColor: isDarkMode ? '#27272a' : '#e2e8f0',
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#0f172a',
                  fontWeight: 'bold',
                  fontSize: '10px',
                  padding: '3px 6px'
                }}
                formatter={(value) => [`${value.toLocaleString()} pts`, 'Earned']}
              />
            )}

            <Bar 
              dataKey="points" 
              radius={[3, 3, 0, 0]}
              isAnimationActive={hasPoints}
            >
              {chartData.map((_, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={BAR_COLORS[index % BAR_COLORS.length]} 
                  className={hasPoints ? "cursor-pointer" : "cursor-default"}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>

        {/* Centered Pill Overlay (Shown only when all months have 0 points) */}
        {!hasPoints && (
          <div className="absolute inset-0 pl-5 flex items-center justify-center pointer-events-none">
            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wide border shadow-sm ${
              isDarkMode 
                ? 'bg-zinc-800/90 text-zinc-400 border-zinc-700/80' 
                : 'bg-zinc-100 text-zinc-500 border-zinc-200'
            }`}>
              no recent Fan points earned
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistMonthlyPointsChart;